// Vínculo do contato com o cadastro (Fase 15).
//
// Duas operações e nada mais: perguntar os candidatos e confirmar um.
//
// A BUSCA passa por edge function, não por consulta do front, porque o
// casamento precisa ler `unidades_consumidoras` e `negociacoes` com
// service_role: o atendente do Inbox pode legitimamente não enxergar aquele
// cadastro pela RLS e ainda assim precisar ligar a conversa a ele. O que volta
// é só id e nome — nunca o cadastro inteiro.
//
// A CONFIRMAÇÃO passa por função do banco, não por UPDATE, porque precisa de
// trilha: ligar a conversa de alguém ao cadastro de outra pessoa é o erro que
// esta tela existe para evitar, e quando acontecer alguém vai precisar saber
// quem confirmou e quando.

import { supabase } from '../../../src/lib/supabase';

export interface CandidatoVinculo {
  tipo: 'unidade_consumidora' | 'negociacao';
  id: string;
  nome: string | null;
  campo: string;
  via: 'telefone' | 'email';
  confianca: number;
  /** Quantos cadastros compartilham o mesmo valor. >1 não identifica ninguém. */
  compartilhadoCom: number;
  motivo: string;
  nomeConfere: 'confere' | 'diverge' | 'indeterminado';
}

export interface RespostaVinculo {
  candidatos: CandidatoVinculo[];
  /** Só o que é seguro destacar: único no tipo, exclusivo, forte e com nome batendo. */
  sugestoes: CandidatoVinculo[];
}

export async function buscarSugestoesVinculo(contactId: string): Promise<RespostaVinculo> {
  const { data, error } = await supabase.functions.invoke('inbox-vinculo-sugestoes', {
    body: { contactId },
  });

  if (error) {
    // `functions.invoke` engole a causa: em resposta não-2xx devolve só
    // "non-2xx status code". O motivo real está em `error.context`.
    const detalhe = await (error as never as { context?: { json?: () => Promise<{ error?: string }> } })
      ?.context?.json?.()
      .catch(() => null);
    throw new Error(detalhe?.error || error.message || 'Falha ao consultar o cadastro.');
  }

  return {
    candidatos: (data?.candidatos ?? []) as CandidatoVinculo[],
    sugestoes: (data?.sugestoes ?? []) as CandidatoVinculo[],
  };
}

/**
 * Grava ou apaga UM dos dois vínculos.
 *
 * O par `p_mexer_*` existe porque, sem ele, "não quero mexer neste campo" e
 * "quero apagar este vínculo" chegariam ao banco do mesmo jeito: null.
 */
export async function confirmarVinculo(
  contactId: string,
  tipo: CandidatoVinculo['tipo'],
  alvoId: number | null,
): Promise<void> {
  const ehUnidade = tipo === 'unidade_consumidora';

  const { data, error } = await supabase.rpc('inbox_vincular_contato', {
    p_contact_id: contactId,
    p_unidade_consumidora_id: ehUnidade ? alvoId : null,
    p_negociacao_id: ehUnidade ? null : alvoId,
    p_mexer_unidade: ehUnidade,
    p_mexer_negociacao: !ehUnidade,
  });

  if (error) throw new Error(error.message);
  if (data && (data as { ok?: boolean; erro?: string }).ok === false) {
    throw new Error((data as { erro?: string }).erro ?? 'Não foi possível vincular.');
  }
}
